import {
  DOCUMENT_FORM_NAME_BASE_ACRES,
  DOCUMENT_FORM_NAME_EQUIPMENT_INVENTORY,
  DOCUMENT_FORM_NAME_FARMS,
  DOCUMENT_FORM_NAME_INCOME_STATEMENT,
  DOCUMENT_FORM_NAME_SCHEDULE_F,
} from "@datanac/datanac-api-toolkit";
import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { DOCUMENT_FORM_NAME_DEBT_SCHEDULE } from "@datanac/datanac-api-toolkit/dist/import/importHelpers";
import { _sessionManager, RmaApiHelper, UsersApiHelper } from "api/ApiHelper";
import {
  DOCUMENT_FORM_NAME_ACTUAL_PRODUCTION_HISTORY,
  DOCUMENT_FORM_NAME_FARMSFSA578FORM,
  FINANCIAL_DOCUMENTS,
} from "components/Financial/FinancialHelpers";
import { UpdateDebtSchedule } from "components/Financial/forms/DebtScheduleHelper";
import { v4 as uuidv4 } from "uuid";

import { overridePracticeName } from "../../farms578Codes";
import { BASE_ACRES_COMMODITY_NAMES_WITH_TYPE } from "../../helpers";

/// Document Chat Assistant: which structured document types expose Import in the footer toolbar.
/** Matches `importTransformedDocument`; keep in sync when adding routes there. */
export const CHAT_ASSISTANT_IMPORTABLE_DOCUMENT_FORM_NAMES = Object.freeze([
  DOCUMENT_FORM_NAME_INCOME_STATEMENT,
  DOCUMENT_FORM_NAME_SCHEDULE_F,
  DOCUMENT_FORM_NAME_BASE_ACRES,
  DOCUMENT_FORM_NAME_FARMSFSA578FORM,
  DOCUMENT_FORM_NAME_FARMS,
  DOCUMENT_FORM_NAME_ACTUAL_PRODUCTION_HISTORY,
]);

export const isChatAssistantImportSupported = (documentType) =>
  !!documentType && CHAT_ASSISTANT_IMPORTABLE_DOCUMENT_FORM_NAMES.includes(documentType);

// Constants for type name fallbacks
const FALLBACK_TYPE_NAME_COMMODITY = "Commodity";
const FALLBACK_TYPE_NAME_NO_TYPE = "No Type Specified";

///
// Helper functions for validating and overriding type_name and practice_name
///

/**
 * Get valid type names from InsuranceOffer API
 */
const getValidTypeNames = async (crop_year, state_name, county_name, commodity_name) => {
  try {
    const insuranceOffers = await RmaApiHelper.selectObject("InsuranceOffer", {
      reinsurance_year: String(crop_year),
      state_name,
      county_name,
      commodity_name,
    });

    // Extract unique type names
    const typeNames = [...new Set(insuranceOffers.map((offer) => offer.type_name).filter(Boolean))];
    return typeNames;
  } catch (error) {
    console.error("Error fetching valid type names:", error);
    return [];
  }
};

/**
 * Get valid practice names from InsuranceOffer API
 */
const getValidPracticeNames = async (crop_year, state_name, county_name, commodity_name, type_name) => {
  try {
    const insuranceOffers = await RmaApiHelper.selectObject("InsuranceOffer", {
      reinsurance_year: String(crop_year),
      state_name,
      county_name,
      commodity_name,
      type_name,
    });

    // Extract unique practice names
    const practiceNames = [...new Set(insuranceOffers.map((offer) => offer.practice_name).filter(Boolean))];
    return practiceNames;
  } catch (error) {
    console.error("Error fetching valid practice names:", error);
    return [];
  }
};

/**
 * Override type_name if not valid
 * Falls through to FALLBACK_TYPE_NAME_COMMODITY or FALLBACK_TYPE_NAME_NO_TYPE
 */
const overrideTypeName = (type_name, validTypeNames) => {
  // If type_name is valid, return as is
  if (validTypeNames.includes(type_name)) {
    return type_name;
  }

  // Try fallback to "Commodity" first
  if (validTypeNames.includes(FALLBACK_TYPE_NAME_COMMODITY)) {
    return FALLBACK_TYPE_NAME_COMMODITY;
  }

  // Fall through to "No Type Specified"
  if (validTypeNames.includes(FALLBACK_TYPE_NAME_NO_TYPE)) {
    return FALLBACK_TYPE_NAME_NO_TYPE;
  }

  // If no fallbacks available, return the first valid type name or original
  return validTypeNames.length > 0 ? validTypeNames[0] : type_name;
};

///
// FileUploadHandlers signature is async(data, apiConfig) => Promise<void>
///
export const FileUploadHandlers = {
  // Base Acres
  [DOCUMENT_FORM_NAME_BASE_ACRES]: {
    handler: UsersApiHelper.updateProducerFarmBaseAcres,
    dataKey: "ProducerFarmBaseAcres",
  },
  // Farms (worksheet name "Farms" from parseExcelForm)
  [DOCUMENT_FORM_NAME_FARMS]: {
    handler: UsersApiHelper.updateProducerFarmFieldCrop,
    dataKey: "ProducerFarmFieldCrops",
    filter: (row) => row.fsa_farm_number && row.commodity_name,
  },
  // FSA 578 Farms
  [DOCUMENT_FORM_NAME_FARMSFSA578FORM]: {
    handler: UsersApiHelper.updateProducerFarmFieldCrop,
    dataKey: "ProducerFarmFieldCrops",
    filter: (row) => row.fsa_farm_number && row.commodity_name,
  },
  // Equipment Inventory
  [DOCUMENT_FORM_NAME_EQUIPMENT_INVENTORY]: {
    handler: UsersApiHelper.updateEquipmentInventory,
    dataKey: "EquipmentInventory",
  },
  // Debt Schedule
  [DOCUMENT_FORM_NAME_DEBT_SCHEDULE]: {
    handler: UpdateDebtSchedule,
    dataKey: "DebtSchedule",
  },
  // Actual Production History
  [DOCUMENT_FORM_NAME_ACTUAL_PRODUCTION_HISTORY]: {
    handler: async (data, globalState) => {
      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });
      await api.callPost("ProducerFarmFieldCropHistory", data, globalState);
    },
    dataKey: "ProducerFarmFieldCropHistory",
  },
};

// Add financial document handlers
FINANCIAL_DOCUMENTS.forEach((doc) => {
  if (!FileUploadHandlers[doc.document_type_name]) {
    FileUploadHandlers[doc.document_type_name] = {
      handler: UsersApiHelper.updateDocumentDictionary,
      dataKey: "Document",
      isFinancialDoc: true,
    };
  }
});

export const ProcessImportFile = (fileContents, globalState, documentId = null, crop_year = null) => {
  const baseFormData = {
    producer_token: globalState.producer_token,
    agent_token: globalState.agent_token,
  };

  const handlerConfig = FileUploadHandlers[fileContents?.formName];
  if (!handlerConfig) {
    throw new Error(`No handler found for form type: ${fileContents?.formName}`);
  }

  let processedDataItems = fileContents?.dataItems || [];

  // Apply filter if specified
  if (handlerConfig.filter) {
    processedDataItems = processedDataItems.filter(handlerConfig.filter);
  }

  // Map data items with required properties
  const mappedDataItems = processedDataItems.map((row) => ({
    id: uuidv4(),
    ...row,
    producer_token: globalState.producer_token,
    agent_token: globalState.agent_token,
    ...(crop_year !== null ? { crop_year } : {}),
  }));

  // Handle special case for financial documents
  if (handlerConfig.isFinancialDoc && documentId) {
    return {
      ...baseFormData,
      [handlerConfig.dataKey]: {
        id: documentId,
        values: mappedDataItems,
      },
    };
  }

  // Handle equipment inventory special case (no producer_token at root level)
  if (fileContents?.formName === DOCUMENT_FORM_NAME_EQUIPMENT_INVENTORY) {
    return {
      producer_token: globalState.producer_token,
      [handlerConfig.dataKey]: mappedDataItems,
    };
  }

  // Standard case
  return {
    ...baseFormData,
    [handlerConfig.dataKey]: mappedDataItems,
  };
};

///
// Import helper functions for specific document types
// These transform Memory API data format to import format
///

export const importTransformedDocument_IncomeStatement = async (documentContents, documentId, globalState) => {
  // Transform Memory API data format to import format
  const fileContents = {
    formName: DOCUMENT_FORM_NAME_INCOME_STATEMENT,
    dataItems: documentContents,
  };

  documentContents.forEach((item) => {
    item.section = item.section || "";
  });

  const uploadFormData = ProcessImportFile(fileContents, globalState, documentId);
  const handlerConfig = FileUploadHandlers[DOCUMENT_FORM_NAME_INCOME_STATEMENT];

  await handlerConfig.handler(uploadFormData, globalState);
};

export const importTransformedDocument_ScheduleF = async (documentContents, documentId, globalState) => {
  // Transform Memory API data format to import format
  const fileContents = {
    formName: DOCUMENT_FORM_NAME_SCHEDULE_F,
    dataItems: documentContents,
  };

  const uploadFormData = ProcessImportFile(fileContents, globalState, documentId);
  const handlerConfig = FileUploadHandlers[DOCUMENT_FORM_NAME_SCHEDULE_F];

  await handlerConfig.handler(uploadFormData, globalState);
};

const normalize = (str) => (str || "").toString().trim().toLowerCase();

/** Base-acres HIP: blank / EMPTY marker → DB null; explicit 0 (number or numeric string) → 0. */
const normalizeHistoricalIrrigationPercentageForImport = (raw) => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? raw : null;
  }
  const s = String(raw).trim();
  if (!s || s.toUpperCase() === "EMPTY") return null;
  const stripped = s.replace(/%/g, "").trim();
  const n = parseFloat(stripped);
  return Number.isFinite(n) ? n : null;
};

const COMMODITY_ALIASES = {
  "seed cotton": { commodity_name: "Cotton" },
  sunflowers: { commodity_name: "Sunflower Seed" },
};

const RICE_PREFIX = "rice";

const RICE_TYPE_ALIASES = {
  "long grain": "Long Grain",
  "medium grain": "Medium Grain",
  "med grain": "Medium Grain",
  "short grain": "Short Grain",
};

const parseRiceCommodity = (rawCommodity) => {
  const rest = rawCommodity
    .slice(RICE_PREFIX.length)
    .replace(/^[\s,\-:;/]+/, "")
    .trim();
  const type = RICE_TYPE_ALIASES[normalize(rest)] || rest || null;
  return { commodity_name: "Rice", type_name: type };
};

const transformBaseAcresCommodity = (item) => {
  const raw = normalize(item.commodity_name);

  const alias = COMMODITY_ALIASES[raw];
  if (alias) return { ...item, ...alias, type_name: null };

  if (raw.startsWith(RICE_PREFIX)) return { ...item, ...parseRiceCommodity(item.commodity_name?.trim() || "") };

  return { ...item, type_name: null };
};

export const importTransformedDocument_BaseAcres = async (
  documentContents,
  documentId,
  globalState,
  crop_year = null
) => {
  const processedContents = await Promise.all(
    documentContents.map(async (item) => {
      const hipNormalized = normalizeHistoricalIrrigationPercentageForImport(item.historical_irrigation_percentage);
      const transformed = transformBaseAcresCommodity({
        ...item,
        historical_irrigation_percentage: hipNormalized,
      });

      const { commodity_name, type_name, location_state_name, location_county_name } = transformed;
      const itemYear = crop_year || transformed.crop_year;

      if (!BASE_ACRES_COMMODITY_NAMES_WITH_TYPE.includes(commodity_name) || !commodity_name) {
        return transformed;
      }

      if (!location_state_name || !location_county_name || !itemYear) {
        return transformed;
      }

      try {
        const validTypeNames = await getValidTypeNames(
          itemYear,
          location_state_name,
          location_county_name,
          commodity_name
        );
        const validatedTypeName = overrideTypeName(type_name, validTypeNames);
        return { ...transformed, type_name: validatedTypeName };
      } catch (error) {
        console.error("Error validating type_name for base acre record:", error);
        return transformed;
      }
    })
  );

  const fileContents = {
    formName: DOCUMENT_FORM_NAME_BASE_ACRES,
    dataItems: processedContents,
  };

  const uploadFormData = ProcessImportFile(fileContents, globalState, documentId, crop_year);
  const handlerConfig = FileUploadHandlers[DOCUMENT_FORM_NAME_BASE_ACRES];

  await handlerConfig.handler(uploadFormData, globalState);
};

export const importTransformedDocument_Farms = async (documentContents, documentId, globalState, crop_year = null) => {
  // Validate and override type_name and practice_name for each record
  const processedContents = await Promise.all(
    documentContents.map(async (item) => {
      const itemYear = crop_year || item.crop_year;
      const location_state_name = item.location_state_name;
      const location_county_name = item.location_county_name;
      const commodity_name = item.commodity_name;

      // Skip validation if missing required fields
      if (!itemYear || !location_state_name || !location_county_name || !commodity_name) {
        return item;
      }

      try {
        // Get valid type names and override if necessary
        const validTypeNames = await getValidTypeNames(
          itemYear,
          location_state_name,
          location_county_name,
          commodity_name
        );
        const validatedTypeName = overrideTypeName(item.type_name, validTypeNames);

        // Get valid practice names and override if necessary
        const validPracticeNames = await getValidPracticeNames(
          itemYear,
          location_state_name,
          location_county_name,
          commodity_name,
          validatedTypeName
        );
        const validatedPracticeName = overridePracticeName(item.practice_name, validPracticeNames, item.type_fac_code);

        return {
          ...item,
          type_name: validatedTypeName,
          practice_name: validatedPracticeName,
        };
      } catch (error) {
        console.error("Error validating type/practice for farm record:", error);
        // Return original item if validation fails
        return item;
      }
    })
  );

  // Transform Memory API data format to import format
  const fileContents = {
    formName: DOCUMENT_FORM_NAME_FARMSFSA578FORM,
    dataItems: processedContents,
  };

  const uploadFormData = ProcessImportFile(fileContents, globalState, documentId, crop_year);
  const handlerConfig = FileUploadHandlers[DOCUMENT_FORM_NAME_FARMSFSA578FORM];

  await handlerConfig.handler(uploadFormData, globalState);
};

export const importTransformedDocument_ActualProductionHistory = async (documentContents, documentId, globalState) => {
  // Transform Memory API data format to import format
  const fileContents = {
    formName: DOCUMENT_FORM_NAME_ACTUAL_PRODUCTION_HISTORY,
    dataItems: documentContents,
  };

  const uploadFormData = ProcessImportFile(fileContents, globalState, documentId);
  const handlerConfig = FileUploadHandlers[DOCUMENT_FORM_NAME_ACTUAL_PRODUCTION_HISTORY];

  await handlerConfig.handler(uploadFormData, globalState);
};

///
// Main import function that routes to specific handlers
// Returns { success: boolean, message: string }
///
const importTransformedDocument_dispatch = async (
  documentType,
  documentContents,
  documentId,
  globalState,
  crop_year
) => {
  switch (documentType) {
    case DOCUMENT_FORM_NAME_INCOME_STATEMENT:
      await importTransformedDocument_IncomeStatement(documentContents, documentId, globalState);
      return { success: true, message: "Income Statement imported successfully" };

    case DOCUMENT_FORM_NAME_SCHEDULE_F:
      await importTransformedDocument_ScheduleF(documentContents, documentId, globalState);
      return { success: true, message: "Schedule F imported successfully" };

    case DOCUMENT_FORM_NAME_BASE_ACRES:
      await importTransformedDocument_BaseAcres(documentContents, documentId, globalState, crop_year);
      return { success: true, message: "Base Acres imported successfully" };

    case DOCUMENT_FORM_NAME_FARMSFSA578FORM:
      await importTransformedDocument_Farms(documentContents, documentId, globalState, crop_year);
      return { success: true, message: "Farms imported successfully" };

    case DOCUMENT_FORM_NAME_FARMS:
      await importTransformedDocument_Farms(documentContents, documentId, globalState, crop_year);
      return { success: true, message: "Farms imported successfully" };

    case DOCUMENT_FORM_NAME_ACTUAL_PRODUCTION_HISTORY:
      await importTransformedDocument_ActualProductionHistory(documentContents, documentId, globalState);
      return { success: true, message: "Actual Production History imported successfully" };

    default:
      return { success: false, message: `Import not yet supported for ${documentType}`, isInfo: true };
  }
};

export const importTransformedDocument = async (
  documentType,
  documentContents,
  documentId,
  globalState,
  crop_year = null
) => {
  if (!documentId || !documentContents || documentContents.length === 0) {
    return { success: false, message: "No document contents to import" };
  }

  try {
    return await importTransformedDocument_dispatch(documentType, documentContents, documentId, globalState, crop_year);
  } catch (error) {
    console.error("Error importing document:", error);
    const detail = error?.response?.data?.detail || error?.detail || error?.message || "";
    return { success: false, message: `Error importing document. Please try again. ${detail}` };
  }
};
